// RoomEntrance — the cinematic "walking into the room" transition.
//
// Contract:
//   · Mounted at app root by CommissionTracker, listens for
//     `hq:enter-room` window events dispatched by any surface that
//     wants a cinematic entrance (Command Center grid cards, deep
//     links, ⌘K palette).
//   · Renders a fullscreen overlay, plays the room's 8s video with
//     amber-tinted vignette and the room's title fading in over it,
//     then dismisses and calls the caller's `onArrive` — typically
//     `setTab(room.tab)`.
//   · Skippable — click / Esc / Enter dismiss immediately and mount
//     the room content.
//   · Respects prefers-reduced-motion — no overlay, straight cut.
//   · Sidebar quick-switches DO NOT use this by design. It's the
//     entrance to a room, not a per-click flourish.
//
// Public API:
//   window.dispatchEvent(new CustomEvent('hq:enter-room', {
//     detail: { roomKey: 'leads', onArrive: () => setTab(...) },
//   }));

import React from 'react';
import { ROOM_VIDEOS } from './roomVideos';
import hq from './theme';

export default function RoomEntrance() {
  const [state, setState] = React.useState({ open: false });
  const videoRef = React.useRef(null);

  const reduced = React.useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const dismiss = React.useCallback(() => {
    setState(s => {
      if (s.open && typeof s.onArrive === 'function') {
        try { s.onArrive(); } catch {}
      }
      return { open: false };
    });
  }, []);

  React.useEffect(() => {
    const onEnter = (e) => {
      const roomKey = e.detail?.roomKey;
      const onArrive = e.detail?.onArrive;
      const room = ROOM_VIDEOS[roomKey];
      if (!room) {
        // Unknown room — fall through to onArrive so the caller isn't
        // stranded on a broken transition.
        try { onArrive?.(); } catch {}
        return;
      }
      if (reduced) {
        try { onArrive?.(); } catch {}
        return;
      }
      setState({ open: true, room, onArrive });
    };
    window.addEventListener('hq:enter-room', onEnter);
    return () => window.removeEventListener('hq:enter-room', onEnter);
  }, [reduced]);

  React.useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open, dismiss]);

  if (!state.open || !state.room) return null;

  const room = state.room;

  return (
    <div
      onClick={dismiss}
      role="dialog"
      aria-label={`Entering ${room.title}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        background: '#050709',
        cursor: 'pointer',
        animation: 'hq-entrance-fade-in .28s ease both',
      }}
    >
      <video
        ref={videoRef}
        src={room.videoUrl}
        autoPlay
        muted
        playsInline
        onEnded={dismiss}
        onError={dismiss}
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',
          filter: 'saturate(1.05)',
        }}
      />

      {/* amber-tinted vignette so the whole overlay reads as one
          building even if the video's palette drifts slightly */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          background:
            'radial-gradient(120% 90% at 50% 100%, rgba(245,166,35,0.10), transparent 60%),' +
            ' linear-gradient(180deg, rgba(5,7,9,0.35) 0%, rgba(5,7,9,0.0) 30%, rgba(5,7,9,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Numbered eyebrow + room title fading in over the video */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: '18%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          animation: 'hq-title-slide-up 1.2s cubic-bezier(.2,.7,.2,1) .35s both',
        }}
      >
        <div
          style={{
            fontFamily: hq.type.mono,
            fontSize: 11,
            letterSpacing: '0.36em',
            color: hq.color.amber,
            marginBottom: 10,
          }}
        >
          {String(room.number).padStart(2, '0')} · ROOM
        </div>
        <div
          style={{
            fontFamily: hq.type.serif,
            fontSize: 'clamp(32px, 5vw, 64px)',
            fontWeight: 600,
            color: hq.color.text,
            letterSpacing: '-0.01em',
            textShadow: '0 6px 40px rgba(0,0,0,0.65)',
          }}
        >
          {room.title}
        </div>
        <div
          style={{
            marginTop: 12,
            fontFamily: hq.type.body,
            fontSize: 14,
            color: hq.color.textSec,
            letterSpacing: '0.02em',
          }}
        >
          {room.tagline}
        </div>
      </div>

      {/* skip affordance */}
      <div
        style={{
          position: 'absolute',
          right: 24, top: 20,
          fontFamily: hq.type.mono,
          fontSize: 10,
          letterSpacing: '0.18em',
          color: hq.color.textMuted,
          textTransform: 'uppercase',
          pointerEvents: 'none',
        }}
      >
        skip · click or esc
      </div>

      <style>{`
        @keyframes hq-entrance-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes hq-title-slide-up {
          from { opacity: 0; transform: translate(-50%, 24px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
